import { default as Mui4Checkbox, CheckboxProps as Mui4CheckboxProps } from '@material-ui/core/Checkbox'
import { makeStyles } from '@material-ui/core/styles'
import CheckIcon from '@material-ui/icons/Check'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import RemoveIcon from '@material-ui/icons/Remove'
import { default as Mui5Checkbox, CheckboxProps as Mui5CheckboxProps } from '@mui/material/Checkbox'
import { useComponentVersion } from '../hooks/useComponentVersion'
import { styled } from '../styles'

const StyledIconBase = styled('span')<CheckboxProps>(({ theme, size, color }) => ({
  borderRadius: 2,
  width: 16,
  height: 16,
  outlineWidth: 1.5,
  outlineStyle: 'solid',
  outlineColor: `${color === 'error' ? theme.palette.red[800] : theme.palette.neutralGrey[800]}`,
  backgroundColor: 'transparent',
  'input:hover + span &': {
    backgroundColor: 'transparent',
    outlineColor: `${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
  },
  'input:focus-visible + span &': {
    outlineColor: `${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
    boxShadow: `0px 0px 0px 6px ${color === 'error' ? theme.palette.red[200] : theme.palette.blue[400]}`,
  },
  'input:disabled + span &': {
    outlineColor: `${color === 'error' ? theme.palette.red[300] : theme.palette.neutralGrey[500]}`,
    cursor: 'not-allowed',
  },
}))

const StyledCheckedIconBase = styled('span')<CheckboxProps>(({ theme, size, color }) => ({
  borderRadius: 2,
  width: 16,
  height: 16,
  outlineWidth: 1.5,
  outlineStyle: 'solid',
  outlineColor: `${color === 'error' ? theme.palette.red[800] : theme.palette.blue[800]}`,
  backgroundColor: `${color === 'error' ? theme.palette.red[800] : theme.palette.blue[800]}`,
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',
  'input:hover + span &': {
    backgroundColor: `${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
    outlineColor: `${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
  },
  'input:focus-visible + span &': {
    backgroundColor: `${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
    outlineColor: `${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
    boxShadow: `0px 0px 0px 6px ${color === 'error' ? theme.palette.red[200] : theme.palette.blue[400]}`,
  },
  'input:disabled + span &': {
    backgroundColor: `${color === 'error' ? theme.palette.red[300] : theme.palette.blue[400]}`,
    outlineColor: `${color === 'error' ? theme.palette.red[300] : theme.palette.blue[400]}`,
    cursor: 'not-allowed',
  },
}))

const StyledCheckIcon = styled(CheckIcon)(({ theme }) => {
  return {
    width: 15,
    height: 15,
    color: theme.palette.white,
    '& path': {
      stroke: theme.palette.white,
      strokeWidth: 1,
    },
  }
})

const StyledIndeterminateIcon = styled(RemoveIcon)(({ theme }) => {
  return {
    width: 15,
    height: 15,
    color: theme.palette.white,
    '& path': {
      stroke: theme.palette.white,
      strokeWidth: 1,
    },
  }
})

const StyledIconContainer = styled('span')<CheckboxProps>(({ theme, size, color }) => ({
  overflow: 'visible',
  display: 'flex',
  padding: 2,
  alignItems: 'center',
  justifyContent: 'center',
}))

const useStyles = makeStyles({
  root: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
    color: 'rgba(0, 0, 0, 0.25)',
  },
  checkedIcon: {
    '&.MuiSvgIcon-root': {
      color: '#1751D0',
    },
  },
})

const V2_CHECKBOX_COLORS = ['info', 'error', 'warning', 'success']

export enum OsCheckboxVersion {
  V1 = 1, // OS UI 2.0
  V2 = 2, // MUI5 + Stratis
}

export type CheckboxProps = Mui5CheckboxProps

export const Checkbox = (props: CheckboxProps) => {
  const classes = useStyles()
  let version = useComponentVersion('checkbox')
  const currentCheckboxSize = 'medium' // In the new Checkbox 3.0 design, the approved size is 'medium' only.
  switch (version) {
    case OsCheckboxVersion.V2:
      return (
        <Mui5Checkbox
          size={currentCheckboxSize}
          icon={
            <StyledIconContainer>
              <StyledIconBase size={currentCheckboxSize} color={props.color || 'primary'} />
            </StyledIconContainer>
          }
          checkedIcon={
            <StyledIconContainer>
              <StyledCheckedIconBase size={currentCheckboxSize} color={props.color || 'primary'}>
                <StyledCheckIcon />
              </StyledCheckedIconBase>
            </StyledIconContainer>
          }
          indeterminateIcon={
            <StyledIconContainer>
              <StyledCheckedIconBase size={currentCheckboxSize} color={props.color || 'primary'}>
                <StyledIndeterminateIcon />
              </StyledCheckedIconBase>
            </StyledIconContainer>
          }
          disableRipple
          {...props}
        />
      )

    default:
      const mui4Props: Mui4CheckboxProps = {
        ...props,
        color:
          !props.color || V2_CHECKBOX_COLORS.includes(props.color)
            ? 'default'
            : (props.color as Mui4CheckboxProps['color']),
        size: props.size === 'large' ? 'medium' : props.size,
      }
      return (
        <Mui4Checkbox
          className={classes.root}
          disableRipple
          checkedIcon={<CheckBoxIcon className={props.checked ? classes.checkedIcon : ''} />}
          inputProps={{ 'aria-label': 'decorative checkbox' }}
          {...mui4Props}
        />
      )
  }
}
